import { FC, useState, useEffect, useMemo, CSSProperties } from 'react';
import {
  SpaceBetween,
  Textarea,
  Button,
  StatusIndicator,
  Box,
  ColumnLayout,
  Grid,
  Toggle,
} from '@amzn/awsui-components-react';
import { diffLines, Change } from 'diff';
import yaml, { JSON_SCHEMA } from 'js-yaml';
import { UatToolCall, UatToolFeedback, JsonValue } from 'src/store/interfaces/Uat.types';

// --- Types ---

interface ToolCallCardProps {
  toolCall: UatToolCall;
  feedback: UatToolFeedback;
  onFeedbackChange: (feedback: UatToolFeedback) => void;
  onYamlTextChange?: (yamlText: string) => void;
  yamlError?: string | null;
  editing: boolean;
}

interface DiffViewProps {
  actualYaml: string;
  correctedYaml: string;
}

// --- Styles ---

const DIFF_COLORS = {
  added: { bg: '#e6ffec', text: '#1a7f37' },
  removed: { bg: '#ffebe9', text: '#cf222e' },
  unchanged: { bg: '#ffffff', text: '#16191f' },
  placeholder: { bg: '#f6f8fa' },
} as const;

const PANEL_STYLE: CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e9ebed',
  borderRadius: 8,
  padding: 12,
};

const PRE_STYLE: CSSProperties = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  lineHeight: '1.6',
  fontSize: 13,
  color: '#16191f',
  background: '#ffffff',
};

// --- Helpers ---

/**
 * Formats tool parameters as YAML with proper indentation.
 * @param toolName - Name of the tool
 * @param params - Tool parameters to format
 * @returns Formatted YAML string
 * @throws Error if YAML serialization fails
 */
export const formatParamsAsYaml = (toolName: string, params: Record<string, JsonValue>): string => {
  try {
    const body = yaml.dump(params, { flowLevel: 1, lineWidth: -1, noRefs: true, schema: JSON_SCHEMA });
    return `${toolName}\n${body
      .split('\n')
      .map((l) => (l ? `  ${l}` : ''))
      .join('\n')
      .trimEnd()}`;
  } catch (error) {
    console.error('Failed to serialize YAML:', error);
    return `${toolName}\n  Error: Unable to format parameters`;
  }
};
const splitLines = (value: string): string[] => value.split('\n').filter(Boolean);

const getToolStatusType = (status: string) =>
  status === 'SUCCESS' ? 'success' : status === 'FAILURE' ? 'error' : 'info';

// --- Diff Components ---

const DiffLine: FC<{ change: Change }> = ({ change }) => {
  const style = change.added
    ? { background: DIFF_COLORS.added.bg, color: DIFF_COLORS.added.text }
    : change.removed
      ? { background: DIFF_COLORS.removed.bg, color: DIFF_COLORS.removed.text }
      : { background: DIFF_COLORS.unchanged.bg, color: DIFF_COLORS.unchanged.text };
  const prefix = change.added ? '+ ' : change.removed ? '- ' : '  ';

  return (
    <span style={{ ...style, display: 'block' }}>
      {splitLines(change.value).map((line, i) => (
        <span key={i} style={{ display: 'block' }}>
          {prefix}
          {line}
        </span>
      ))}
    </span>
  );
};

const SideBySideDiffPanel: FC<{
  changes: Change[];
  side: 'actual' | 'corrected';
  label: string;
}> = ({ changes, side, label }) => {
  const isSkipped = side === 'actual' ? 'added' : 'removed';
  const isHighlighted = side === 'actual' ? 'removed' : 'added';

  return (
    <div>
      <Box fontSize="body-s" fontWeight="bold" padding={{ bottom: 'xxs' }} color="text-body-secondary">
        {label}
      </Box>
      <div style={PANEL_STYLE}>
        <pre style={PRE_STYLE}>
          {changes.map((change, i) =>
            change[isSkipped as keyof Change] ? (
              <span key={i} style={{ display: 'block' }}>
                {splitLines(change.value).map((_, j) => (
                  <span key={j} style={{ display: 'block', background: DIFF_COLORS.placeholder.bg }}>
                    &nbsp;
                  </span>
                ))}
              </span>
            ) : (
              <span key={i} style={{ display: 'block' }}>
                {splitLines(change.value).map((line, j) => {
                  const highlighted = !!change[isHighlighted as keyof Change];
                  return (
                    <span
                      key={j}
                      style={{
                        display: 'block',
                        background: highlighted ? DIFF_COLORS[isHighlighted].bg : DIFF_COLORS.unchanged.bg,
                        color: highlighted ? DIFF_COLORS[isHighlighted].text : DIFF_COLORS.unchanged.text,
                      }}
                    >
                      {highlighted ? (side === 'actual' ? '− ' : '+ ') : '  '}
                      {line}
                    </span>
                  );
                })}
              </span>
            ),
          )}
        </pre>
      </div>
    </div>
  );
};

const SideBySideDiffView: FC<DiffViewProps> = ({ actualYaml, correctedYaml }) => {
  const changes = useMemo(() => diffLines(actualYaml, correctedYaml), [actualYaml, correctedYaml]);

  return (
    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
      <SideBySideDiffPanel changes={changes} side="actual" label="Actual" />
      <SideBySideDiffPanel changes={changes} side="corrected" label="Corrected" />
    </Grid>
  );
};

const UnifiedDiffView: FC<DiffViewProps> = ({ actualYaml, correctedYaml }) => {
  const changes = useMemo(() => diffLines(actualYaml, correctedYaml), [actualYaml, correctedYaml]);

  return (
    <div style={PANEL_STYLE}>
      <pre style={PRE_STYLE}>
        {changes.map((change, i) => (
          <DiffLine key={i} change={change} />
        ))}
      </pre>
    </div>
  );
};

// --- Main Component ---

const ToolCallCard: FC<ToolCallCardProps> = ({ toolCall, feedback, onFeedbackChange, onYamlTextChange, yamlError, editing }) => {
  const [correctedYamlText, setCorrectedYamlText] = useState('');
  const [sideBySide, setSideBySide] = useState(true);

  const actualYaml = useMemo(
    () => formatParamsAsYaml(toolCall.toolName, toolCall.toolParameters),
    [toolCall.toolName, toolCall.toolParameters],
  );

  const correctedYaml = useMemo(
    () =>
      feedback.correctedParameters
        ? formatParamsAsYaml(toolCall.toolName, feedback.correctedParameters)
        : actualYaml,
    [toolCall.toolName, feedback.correctedParameters, actualYaml],
  );

  const actualParamsYaml = useMemo(
    () => yaml.dump(toolCall.toolParameters, { flowLevel: 1, lineWidth: -1, noRefs: true, schema: JSON_SCHEMA }),
    [toolCall.toolParameters],
  );

  useEffect(() => {
    const text = feedback.correctedParameters
      ? yaml.dump(feedback.correctedParameters, { flowLevel: 1, lineWidth: -1, noRefs: true, schema: JSON_SCHEMA })
      : actualParamsYaml;
    setCorrectedYamlText(text);
    onYamlTextChange?.(text);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualParamsYaml, feedback.correctedParameters]);

  const handleCorrectedYamlChange = (value: string) => {
    setCorrectedYamlText(value);
    onYamlTextChange?.(value);
  };

  const handleReset = () => {
    setCorrectedYamlText(actualParamsYaml);
    onYamlTextChange?.(actualParamsYaml);
    onFeedbackChange({ ...feedback, correctedParameters: null });
  };

  const handleVote = (vote: 'up' | 'down') => {
    onFeedbackChange({ ...feedback, vote: feedback.vote === vote ? null : vote });
  };

  const hasCorrectedParams = !!feedback.correctedParameters;

  return (
    <SpaceBetween size="xs">
      {/* Header row: tool name, status, feedback */}
      <Box padding={{ left: 'l' }}>
        <ColumnLayout columns={editing ? 3 : hasCorrectedParams ? 3 : 2} variant="text-grid">
          <div>
            <Box fontSize="body-s" display="inline" color="text-body-secondary">
              Tool Name:{' '}
            </Box>
            <Box fontSize="heading-s" display="inline" fontWeight="bold">
              {toolCall.toolName}
            </Box>
          </div>
          <div>
            <Box fontSize="body-s" display="inline" color="text-body-secondary">
              Status:{' '}
            </Box>
            <StatusIndicator type={getToolStatusType(toolCall.status)}>{toolCall.status}</StatusIndicator>
          </div>
          {editing ? (
            <div>
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant={feedback.vote === 'up' ? 'primary' : 'normal'}
                  iconName="thumbs-up"
                  onClick={() => handleVote('up')}
                >
                  Correct
                </Button>
                <Button
                  variant={feedback.vote === 'down' ? 'primary' : 'normal'}
                  iconName="thumbs-down"
                  onClick={() => handleVote('down')}
                >
                  Incorrect
                </Button>
              </SpaceBetween>
            </div>
          ) : (
            feedback.vote && (
              <div>
                <Box fontSize="body-s" display="inline" color="text-body-secondary">
                  Feedback:{' '}
                </Box>
                <StatusIndicator type={feedback.vote === 'up' ? 'success' : 'error'}>
                  {feedback.vote === 'up' ? 'Correct' : 'Incorrect'}
                </StatusIndicator>
              </div>
            )
          )}
        </ColumnLayout>
      </Box>

      {/* Parameters section */}
      <Box padding={{ left: 'l' }}>
        <SpaceBetween size="xs">
          {/* Read-only mode */}
          {!editing && (
            <SpaceBetween size="xs">
              {hasCorrectedParams && (
                <Toggle checked={sideBySide} onChange={({ detail }) => setSideBySide(detail.checked)}>
                  Side-by-side
                </Toggle>
              )}
              {hasCorrectedParams && sideBySide ? (
                <SideBySideDiffView actualYaml={actualYaml} correctedYaml={correctedYaml} />
              ) : hasCorrectedParams ? (
                <UnifiedDiffView actualYaml={actualYaml} correctedYaml={correctedYaml} />
              ) : (
                <div style={PANEL_STYLE}>
                  <pre style={PRE_STYLE}>{actualYaml}</pre>
                </div>
              )}
            </SpaceBetween>
          )}

          {/* Editing mode: YAML preview (read-only) + JSON editor */}
          {editing && (
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <div>
                <Box fontSize="body-s" fontWeight="bold" padding={{ bottom: 'xxs' }} color="text-body-secondary">
                  Actual Parameters
                </Box>
                <div style={PANEL_STYLE}>
                  <pre style={PRE_STYLE}>{actualYaml}</pre>
                </div>
              </div>
              <div>
                <SpaceBetween size="xs">
                  <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">
                    Corrected Parameters (YAML)
                  </Box>
                  <Textarea
                    value={correctedYamlText}
                    onChange={({ detail }) => handleCorrectedYamlChange(detail.value)}
                    rows={8}
                    invalid={!!yamlError}
                    disabled={feedback.vote !== 'down'}
                  />
                  {yamlError && (
                    <Box color="text-status-error" fontSize="body-s">
                      {yamlError}
                    </Box>
                  )}
                  <Button variant="normal" iconName="undo" onClick={handleReset} disabled={feedback.vote !== 'down'}>
                    Reset to actual
                  </Button>
                </SpaceBetween>
              </div>
            </Grid>
          )}
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
};

export default ToolCallCard;
